
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";
import WebsiteAnalysisHeader from "./WebsiteAnalysisHeader";
import LastAnalyzedInfo from "./LastAnalyzedInfo";
import AnalysisStatus from "./AnalysisStatus";
import { useBusinessWebsite } from "@/hooks/business/useBusinessWebsite";

interface WebsiteAnalysisSectionProps {
  website?: string;
  lastAnalyzedAt?: number;
  updateBusinessData: (key: string, value: string | string[] | number | Record<string, string>) => void;
  updateMultipleFields?: (updates: Record<string, string | string[] | number | Record<string, string>>) => void;
}

const WebsiteAnalysisSection = ({ 
  website = "",
  lastAnalyzedAt,
  updateBusinessData,
  updateMultipleFields = () => {}
}: WebsiteAnalysisSectionProps) => {
  const businessData = { website, lastAnalyzedAt };
  
  const { 
    websiteInput,
    handleWebsiteChange,
    saveWebsite,
    analyzeWebsite,
    isAnalyzing,
    canAnalyzeWebsite,
    analysisError,
    analysisSuccess
  } = useBusinessWebsite({
    businessData: businessData as any,
    updateBusinessData,
    updateMultipleFields
  });

  const handleAnalyzeClick = async () => {
    saveWebsite();
    await analyzeWebsite();
  };

  return (
    <div>
      <WebsiteAnalysisHeader 
        title="Website Analysis" 
        description="Enter your website URL to automatically extract business information and improve your AI Staff's understanding of your business."
      />
      
      <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <div className="relative flex-1 w-full">
          <Input
            type="url"
            placeholder="https://yourbusiness.com"
            value={websiteInput}
            onChange={(e) => handleWebsiteChange(e.target.value)}
            className="pr-10 w-full"
            onBlur={saveWebsite}
          />
        </div>
        
        <Button
          onClick={handleAnalyzeClick}
          disabled={isAnalyzing || !websiteInput}
          variant="default"
          className="whitespace-nowrap"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              Analyze Website
            </>
          )}
        </Button>
      </div>
      
      <AnalysisStatus 
        error={analysisError}
        success={analysisSuccess}
      />
      
      {lastAnalyzedAt && (
        <LastAnalyzedInfo lastAnalyzedAt={lastAnalyzedAt} />
      )}
    </div>
  );
};

export default WebsiteAnalysisSection;
